"use client"

import * as React from "react"

import { AskLeoButton } from "@/components/ask-leo-button"
import { Button } from "@/components/ui/button"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { LeoSpottedHeader } from "@/components/question-quality/leo-spotted-header"
import {
  LEO_SPOTTED_SURFACE_WASH,
  QuestionQualityFinding,
  QuestionQualitySuggestedEnhancements,
} from "@/components/question-quality/question-quality-finding"
import { cn } from "@/lib/utils"
import {
  authoringConfidenceScoreLineItem,
  clampQualityHoverWidth,
  LEO_MODIFY_WITH_LEO_LABEL,
  leoModifyFindingAria,
  QUALITY_HOVER_MAX_WIDTH_CSS,
  collapseQuestionQualityHome,
  isSettingSuggestionCheck,
  questionQualityFieldSiblings,
  selectHomeRecommendations,
  settingApplyLabel,
  isQualityFocusOnCheck,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
} from "@/lib/question-quality"
import { measureMarkedText, type MarkRect } from "@/lib/question-quality/measure-mark"

function useMarkedTextRects(
  layerRef: React.RefObject<HTMLElement | null>,
  markText: string | undefined,
  sourceText: string | undefined,
) {
  const [rects, setRects] = React.useState<MarkRect[]>([])
  const [ready, setReady] = React.useState(false)

  React.useLayoutEffect(() => {
    const layer = layerRef.current
    const root = layer?.parentElement
    if (!root || !markText) {
      setRects([])
      setReady(true)
      return
    }

    let cancelled = false
    const measure = () => {
      if (cancelled) return
      const next = measureMarkedText(root, markText)
      setRects(next)
      if (next.length > 0) setReady(true)
    }

    setReady(false)
    measure()
    const frame = window.requestAnimationFrame(() => {
      measure()
      window.requestAnimationFrame(measure)
    })
    const settle = window.setTimeout(() => {
      if (!cancelled) {
        measure()
        setReady(true)
      }
    }, 240)
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    const mutations = new MutationObserver((records) => {
      if (records.every((record) => layer.contains(record.target))) return
      measure()
    })
    mutations.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    window.addEventListener("resize", measure)
    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame)
      window.clearTimeout(settle)
      observer.disconnect()
      mutations.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [layerRef, markText, sourceText])

  return { rects, ready }
}

function useFieldHoverWidth(layerRef: React.RefObject<HTMLElement | null>) {
  const [width, setWidth] = React.useState<number | undefined>(undefined)

  React.useLayoutEffect(() => {
    const field = layerRef.current?.parentElement
    if (!field) return

    const measure = () => {
      const rem =
        Number.parseFloat(getComputedStyle(document.documentElement).fontSize) ||
        16
      setWidth(
        clampQualityHoverWidth(
          field.getBoundingClientRect().width,
          window.innerWidth,
          rem,
        ),
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(field)
    window.addEventListener("resize", measure)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [layerRef])

  return width
}

const QUALITY_HOVER_CONTENT_CLASS =
  "@container/qq-hover relative z-[100] flex max-h-[min(36rem,var(--radix-hover-card-content-available-height,calc(100dvh-2rem)))] w-[min(40rem,calc(100vw-2rem))] max-w-none flex-col overflow-hidden p-0 shadow-xl"

export const SETTING_QUALITY_HOVER_WIDTH = 280

export const SETTING_QUALITY_HOVER_CONTENT_CLASS =
  "@container/qq-hover relative z-[100] flex max-h-[min(28rem,var(--radix-hover-card-content-available-height,calc(100dvh-2rem)))] w-[min(17.5rem,calc(100vw-2rem))] max-w-none flex-col overflow-hidden p-0 shadow-xl"

export function QualityHoverCardContent({
  hoverWidth,
  children,
  onPointerEnter,
  onPointerLeave,
  className,
}: {
  hoverWidth?: number
  children: React.ReactNode
  onPointerEnter?: React.PointerEventHandler<HTMLDivElement>
  onPointerLeave?: React.PointerEventHandler<HTMLDivElement>
  className?: string
}) {
  return (
    <HoverCardContent
      align="start"
      side="bottom"
      sideOffset={16}
      collisionPadding={16}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={cn(QUALITY_HOVER_CONTENT_CLASS, className)}
      style={{
        width: hoverWidth,
        maxWidth: QUALITY_HOVER_MAX_WIDTH_CSS,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 forced-colors:hidden"
        style={{
          background:
            "radial-gradient(ellipse 120% 80% at 50% 100%, oklch(from var(--brand-color) l c h / 0.08) 0%, transparent 68%)",
        }}
      />
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {children}
      </div>
    </HoverCardContent>
  )
}

export function QuestionQualityCardBody({
  check,
  onUse,
  onDismiss,
  onOpenPane,
  onModify,
  onClose,
  disabled,
  siblingChecks,
  layout = "popover",
  outlineApplyCta,
  compact = false,
}: {
  check: QuestionQualityCheck
  onUse: (
    check: QuestionQualityCheck,
    secondary?: boolean,
    alternate?: QuestionQualityAlternate,
  ) => void
  onDismiss: (check: QuestionQualityCheck) => void
  onOpenPane?: (check: QuestionQualityCheck) => void
  onModify?: (
    check: QuestionQualityCheck,
    alternate: QuestionQualityAlternate,
  ) => void
  onClose?: () => void
  disabled?: boolean
  openCheckCount?: number
  siblingChecks?: QuestionQualityCheck[]
  layout?: "stack" | "popover"
  /** Hover popovers use outline apply CTAs until selected. */
  outlineApplyCta?: boolean
  /** Inspector setting marks: tighter shell aligned to field labels. */
  compact?: boolean
}) {
  const openChecks =
    siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check]
  const fieldSiblings = questionQualityFieldSiblings(openChecks, check)
  const home =
    collapseQuestionQualityHome(fieldSiblings, openChecks) ?? check
  const textRecs = selectHomeRecommendations(fieldSiblings, openChecks)
  const nowScore = authoringConfidenceScoreLineItem(home)
  const openLeo = () => {
    onClose?.()
    ;(onOpenPane ?? ((item) => onUse(item)))(home)
  }
  const popover = layout === "popover"
  const outlineCta = outlineApplyCta ?? popover
  const settingSuggestion = isSettingSuggestionCheck(home)

  return (
    <div className={cn(popover && "flex min-h-0 min-w-0 flex-1 flex-col")}>
      {popover ? (
        <LeoSpottedHeader
          nowScore={nowScore}
          className={compact ? "px-3 pb-2.5 pt-2.5" : undefined}
        />
      ) : null}
      <div
        className={cn(
          popover
            ? cn(
                "min-h-0 flex-1 overflow-y-auto overscroll-contain",
                compact
                  ? "space-y-3 px-3 pb-2.5 pt-2.5"
                  : "space-y-4 px-3.5 pb-3 pt-3",
              )
            : "space-y-4",
        )}
      >
        <QuestionQualitySuggestedEnhancements
          check={home}
          siblingChecks={openChecks}
          recs={textRecs}
          onUse={(secondary, alternate) => onUse(home, secondary, alternate)}
          onDismissCard={() => {
            onClose?.()
            for (const item of fieldSiblings) onDismiss(item)
          }}
          disabled={disabled}
          outlineApplyCta={outlineCta}
          compact={compact}
          resolveActionLabel={
            settingSuggestion ? settingApplyLabel : undefined
          }
        />
        <QuestionQualityFinding
          check={home}
          siblingChecks={openChecks}
          surface="hover"
          bleedTop={false}
          quiet={textRecs.length > 0}
        />
      </div>
      <footer
        className={cn(
          "flex flex-wrap items-center justify-end gap-2",
          popover
            ? cn("shrink-0", compact ? "px-3 pb-3 pt-0" : "px-3.5 pb-3.5 pt-1")
            : "pt-4",
        )}
      >
        <AskLeoButton
          size="sm"
          variant="outline"
          showShortcut={false}
          animatedStar={false}
          className="w-fit bg-transparent"
          label={LEO_MODIFY_WITH_LEO_LABEL}
          ariaLabel={leoModifyFindingAria(home.title)}
          tooltipLabel={LEO_MODIFY_WITH_LEO_LABEL}
          disabled={disabled}
          onClick={openLeo}
        />
      </footer>
    </div>
  )
}

function QuestionQualityHoverCard({
  hoverWidth,
  children,
  ...body
}: {
  hoverWidth?: number
  children: React.ReactNode
} & Omit<React.ComponentProps<typeof QuestionQualityCardBody>, "layout" | "onClose">) {
  const [open, setOpen] = React.useState(false)
  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={80} closeDelay={120}>
      {children}
      <QualityHoverCardContent hoverWidth={hoverWidth}>
        <QuestionQualityCardBody
          {...body}
          layout="popover"
          onClose={() => setOpen(false)}
        />
      </QualityHoverCardContent>
    </HoverCard>
  )
}

function markAriaLabel(check: QuestionQualityCheck) {
  const severity = check.severity === "review" ? "Review" : "Caution"
  return `${severity}: ${check.title}. Open suggestion.`
}

function markTriggerClassName(className?: string) {
  return cn(
    "group/mark appearance-none bg-transparent p-0 pointer-events-auto min-h-6 min-w-6 overflow-visible rounded-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    className,
  )
}

const LEO_MARK_UNDERLINE =
  "bg-[var(--exxat-color-danger,var(--destructive))]"
const LEO_MARK_UNDERLINE_LIGHT =
  "bg-[color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_32%,transparent)]"

function MarkDecor({ pagerActive = false }: { pagerActive?: boolean }) {
  return (
    <>
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-sm",
          LEO_SPOTTED_SURFACE_WASH,
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-0.5 transition-colors duration-150",
          pagerActive
            ? LEO_MARK_UNDERLINE
            : cn(
                LEO_MARK_UNDERLINE_LIGHT,
                "group-hover/mark:bg-[var(--exxat-color-danger,var(--destructive))]",
                "group-focus-visible/mark:bg-[var(--exxat-color-danger,var(--destructive))]",
                "group-data-[state=open]/mark:bg-[var(--exxat-color-danger,var(--destructive))]",
              ),
        )}
      />
    </>
  )
}

export function QuestionQualityMark({
  check,
  onUse,
  onDismiss,
  onOpenPane,
  onModify,
  disabled,
  className,
  sourceText,
  openCheckCount,
  siblingChecks,
  focusCheckId = null,
}: {
  check: QuestionQualityCheck
  onUse: (
    check: QuestionQualityCheck,
    secondary?: boolean,
    alternate?: QuestionQualityAlternate,
  ) => void
  onDismiss: (check: QuestionQualityCheck) => void
  onOpenPane: (check: QuestionQualityCheck) => void
  onModify?: (
    check: QuestionQualityCheck,
    alternate: QuestionQualityAlternate,
  ) => void
  disabled?: boolean
  className?: string
  sourceText?: string
  openCheckCount?: number
  siblingChecks?: QuestionQualityCheck[]
  /** Ask Leo pager focus — full underline on the card’s canvas field. */
  focusCheckId?: string | null
}) {
  const layerRef = React.useRef<HTMLDivElement>(null)
  const openChecks =
    siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check]
  const fieldFocused = isQualityFocusOnCheck(openChecks, focusCheckId, check)
  const pagerActive = Boolean(focusCheckId) && fieldFocused
  const { rects, ready } = useMarkedTextRects(
    layerRef,
    check.markText,
    sourceText,
  )
  const hoverWidth = useFieldHoverWidth(layerRef)
  const home =
    collapseQuestionQualityHome(
      questionQualityFieldSiblings(
        siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check],
        check,
      ),
      siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check],
    ) ?? check

  const body = {
    check,
    onUse,
    onDismiss,
    onOpenPane,
    onModify,
    disabled,
    openCheckCount,
    siblingChecks,
  }

  return (
    <div
      ref={layerRef}
      className={cn(
        "pointer-events-none absolute inset-0 z-10",
        fieldFocused && "z-20",
        className,
      )}
    >
      {!ready ? null : rects.length > 0 ? (
        rects.map((rect, index) => (
          <QuestionQualityHoverCard
            key={`${check.id}-${index}`}
            hoverWidth={hoverWidth}
            {...body}
          >
            <HoverCardTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label={markAriaLabel(home)}
                className={markTriggerClassName("absolute")}
                style={{
                  top: rect.top,
                  left: rect.left,
                  width: Math.max(rect.width, 24),
                  height: Math.max(rect.height, 24),
                }}
              >
                <MarkDecor pagerActive={pagerActive} />
              </button>
            </HoverCardTrigger>
          </QuestionQualityHoverCard>
        ))
      ) : (
        <QuestionQualityHoverCard hoverWidth={hoverWidth} {...body}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={markAriaLabel(home)}
              className={markTriggerClassName(
                "absolute bottom-2 start-3 z-10 px-2 text-xs font-medium",
              )}
            >
              <MarkDecor pagerActive={pagerActive} />
              <span className="relative">{home.title}</span>
            </button>
          </HoverCardTrigger>
        </QuestionQualityHoverCard>
      )}
    </div>
  )
}

export function QuestionQualityRowWash({
  children,
}: {
  severity?: QuestionQualityCheck["severity"]
  children: React.ReactNode
}) {
  return <div className="relative">{children}</div>
}

export function questionQualityMarkClass(
  _severity?: QuestionQualityCheck["severity"],
) {
  return LEO_SPOTTED_SURFACE_WASH
}
